import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Mic, Plus, Settings, Volume2, X } from "lucide-react";
import toast from "react-hot-toast";
import DevicePicker from "../../components/common/DevicePicker";
import { mediaDeviceService } from "../../services/media/device.service";

interface AudioProcessing {
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  backgroundBlur: boolean;
  hdVideo: boolean;
}

interface FrameConfig {
  id: string;
  name: string;
  cameraId: string;
  microphoneId: string;
}

const uid = () => Math.random().toString(36).slice(2, 9);

// Opening several USB cameras at the exact same instant makes some drivers
// return NotReadableError. Serialize every getUserMedia call so cameras open
// one after another.
let mediaQueue: Promise<unknown> = Promise.resolve();
function enqueueMedia<T>(fn: () => Promise<T>): Promise<T> {
  const run = mediaQueue.then(fn, fn);
  mediaQueue = run.catch(() => undefined);
  return run;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface AcquireArgs {
  cameraId: string;
  microphoneId: string;
  hdVideo: boolean;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
}

// Try progressively looser video constraints so identical / bandwidth-limited
// USB cameras still open instead of hard-failing on the exact HD request.
async function acquireFrameStream(args: AcquireArgs): Promise<MediaStream> {
  const audio: MediaTrackConstraints = {
    deviceId: args.microphoneId ? { exact: args.microphoneId } : undefined,
    echoCancellation: args.echoCancellation,
    noiseSuppression: args.noiseSuppression,
    autoGainControl: args.autoGainControl,
  };

  // Always keep deviceId EXACT — a non-exact id lets the browser fall back to
  // the default camera, which would make every frame show the same video.
  // Only relax resolution so bandwidth-limited USB cameras still open.
  const videoTiers: MediaTrackConstraints[] = args.cameraId
    ? [
        {
          deviceId: { exact: args.cameraId },
          width: { ideal: args.hdVideo ? 1280 : 640 },
          height: { ideal: args.hdVideo ? 720 : 480 },
        },
        { deviceId: { exact: args.cameraId }, width: { ideal: 640 }, height: { ideal: 480 } },
        { deviceId: { exact: args.cameraId } },
      ]
    : [{ width: { ideal: 640 } }];

  let lastErr: unknown;
  for (const video of videoTiers) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        return await navigator.mediaDevices.getUserMedia({ video, audio });
      } catch (err) {
        lastErr = err;
        // Device busy releasing a previous handle — wait then retry once.
        if ((err as DOMException)?.name === "NotReadableError") await sleep(350);
      }
    }
  }
  throw lastErr;
}

const HostPreJoin = () => {
  const navigate = useNavigate();

  const [meetingTitle, setMeetingTitle] = useState("Court Hearing");
  const [meetingId, setMeetingId] = useState("");
  const [speakerId, setSpeakerId] = useState("");
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [audio, setAudio] = useState<AudioProcessing>({
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    backgroundBlur: true,
    hdVideo: true,
  });

  const [frames, setFrames] = useState<FrameConfig[]>([]);
  const [ready, setReady] = useState(false);

  // Auto-detect: one frame per physical camera on first load.
  useEffect(() => {
    let cancelled = false;

    async function detect() {
      try {
        // Prompt once so device labels/ids are available.
        const probe = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        probe.getTracks().forEach((t) => t.stop());
      } catch {
        // Permission denied — still render, frames will show an error.
      }

      const [cams, mics, spks] = await Promise.all([
        mediaDeviceService.cameras(),
        mediaDeviceService.microphones(),
        mediaDeviceService.speakers(),
      ]);
      if (cancelled) return;

      setSpeakers(spks);
      setSpeakerId(spks[0]?.deviceId || "");

      const defaultMic = mics[0]?.deviceId || "";
      const detected: FrameConfig[] = (cams.length ? cams : [null]).map(
        (cam, index) => ({
          id: uid(),
          name: index === 0 ? "Judge Camera" : `Camera ${index + 1}`,
          cameraId: cam?.deviceId || "",
          microphoneId: defaultMic,
        }),
      );

      setFrames(detected);
      setReady(true);
    }

    void detect();
    return () => {
      cancelled = true;
    };
  }, []);

  const addFrame = useCallback(async () => {
    const cams = await mediaDeviceService.cameras();
    const used = new Set(frames.map((f) => f.cameraId));
    const nextCam = cams.find((c) => !used.has(c.deviceId)) || cams[0];
    const mics = await mediaDeviceService.microphones();

    setFrames((prev) => [
      ...prev,
      {
        id: uid(),
        name: `Camera ${prev.length + 1}`,
        cameraId: nextCam?.deviceId || "",
        microphoneId: mics[0]?.deviceId || "",
      },
    ]);
  }, [frames]);

  const removeFrame = (id: string) =>
    setFrames((prev) => prev.filter((f) => f.id !== id));

  const updateFrame = (id: string, patch: Partial<FrameConfig>) =>
    setFrames((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const handleStart = () => {
    if (!meetingTitle.trim()) return toast.error("Meeting title is required.");
    if (frames.length === 0) return toast.error("Add at least one camera frame.");

    navigate("/advocate-lobby", {
      state: {
        meetingTitle: meetingTitle.trim(),
        meetingId: meetingId.trim(),
        speakerId,
        audio,
        frames,
      },
    });
  };

  return (
    <div className="min-h-full bg-[#0b0f1a] text-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <div>
          <h1 className="text-2xl font-bold">OneMeet</h1>
          <p className="text-sm text-slate-400">Host Pre-Join</p>
        </div>
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-white transition hover:bg-white/20"
          aria-label="Settings"
        >
          <Settings className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 p-5 xl:grid-cols-[1fr_360px]">
        {/* Camera frames */}
        <div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 2xl:grid-cols-3">
            {frames.map((frame) => (
              <FrameCard
                key={frame.id}
                frame={frame}
                audio={audio}
                removable={frames.length > 1}
                onRemove={() => removeFrame(frame.id)}
                onName={(name) => updateFrame(frame.id, { name })}
                onCamera={(d) => updateFrame(frame.id, { cameraId: d.deviceId })}
                onMic={(d) => updateFrame(frame.id, { microphoneId: d.deviceId })}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={addFrame}
            className="mt-5 flex w-full max-w-md items-center justify-center gap-2 rounded-2xl border border-dashed border-white/20 bg-white/5 px-4 py-5 font-semibold text-white transition hover:bg-white/10"
          >
            <span className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 hover:bg-blue-700">
              <Plus className="h-5 w-5" /> Add More Frame
            </span>
          </button>
        </div>

        {/* Right panel */}
        <div className="flex flex-col gap-5">
          {/* Meeting info */}
          <Panel title="Meeting Information">
            <input
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.target.value)}
              placeholder="Meeting title"
              className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none focus:border-blue-500"
            />
            <input
              value={meetingId}
              onChange={(e) => setMeetingId(e.target.value)}
              placeholder="Meeting ID"
              className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none focus:border-blue-500"
            />
          </Panel>

          {/* Speaker */}
          <Panel
            title={
              <span className="flex items-center gap-2">
                <Volume2 className="h-4 w-4" /> Speaker
              </span>
            }
          >
            <select
              value={speakerId}
              onChange={(e) => setSpeakerId(e.target.value)}
              disabled={speakers.length === 0}
              className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none focus:border-blue-500 disabled:opacity-50"
            >
              {speakers.length === 0 && <option value="">No speaker found</option>}
              {speakers.map((s, i) => (
                <option key={s.deviceId || i} value={s.deviceId} className="bg-slate-800">
                  {s.label || `Speaker ${i + 1}`}
                </option>
              ))}
            </select>
          </Panel>

          {/* Audio processing */}
          <Panel title="Audio Processing">
            <ToggleRow label="Echo Cancellation" checked={audio.echoCancellation} onChange={(v) => setAudio((a) => ({ ...a, echoCancellation: v }))} />
            <ToggleRow label="Noise Suppression" checked={audio.noiseSuppression} onChange={(v) => setAudio((a) => ({ ...a, noiseSuppression: v }))} />
            <ToggleRow label="Auto Gain Control" checked={audio.autoGainControl} onChange={(v) => setAudio((a) => ({ ...a, autoGainControl: v }))} />
            <ToggleRow label="Background Blur" checked={audio.backgroundBlur} onChange={(v) => setAudio((a) => ({ ...a, backgroundBlur: v }))} />
            <ToggleRow label="HD Video" checked={audio.hdVideo} onChange={(v) => setAudio((a) => ({ ...a, hdVideo: v }))} />
          </Panel>
        </div>
      </div>

      {/* Footer actions */}
      <div className="sticky bottom-0 flex justify-end gap-3 border-t border-white/10 bg-[#0b0f1a]/95 px-5 py-4 backdrop-blur">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-xl bg-white/10 px-6 py-2.5 font-semibold text-white transition hover:bg-white/20"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleStart}
          disabled={!ready}
          className="rounded-xl bg-blue-600 px-6 py-2.5 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
        >
          Start Meeting
        </button>
      </div>
    </div>
  );
};

export default HostPreJoin;

/* ---------------- Frame card ---------------- */

function FrameCard({
  frame,
  audio,
  removable,
  onRemove,
  onName,
  onCamera,
  onMic,
}: {
  frame: FrameConfig;
  audio: AudioProcessing;
  removable: boolean;
  onRemove: () => void;
  onName: (name: string) => void;
  onCamera: (device: MediaDeviceInfo) => void;
  onMic: (device: MediaDeviceInfo) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState("");

  const { cameraId, microphoneId } = frame;
  const { hdVideo, echoCancellation, noiseSuppression, autoGainControl } = audio;

  // Acquire this frame's own stream whenever its devices / constraints change.
  useEffect(() => {
    let cancelled = false;

    async function start() {
      // Release this frame's previous handle before requesting a new one.
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;

      try {
        const stream = await enqueueMedia(() =>
          acquireFrameStream({
            cameraId,
            microphoneId,
            hdVideo,
            echoCancellation,
            noiseSuppression,
            autoGainControl,
          }),
        );
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setError("");
      } catch (err) {
        console.error("frame getUserMedia failed", err);
        if (!cancelled) setError("Camera unavailable");
      }
    }

    void start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [cameraId, microphoneId, hdVideo, echoCancellation, noiseSuppression, autoGainControl]);

  return (
    <div>
      <label className="mb-1 block text-sm text-slate-400">Name of the frame</label>
      <input
        value={frame.name}
        onChange={(e) => onName(e.target.value)}
        placeholder="Frame name"
        className="mb-2 h-11 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none focus:border-blue-500"
      />

      <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-slate-900">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="h-full w-full object-cover"
        />
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
            {error}
          </div>
        )}
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove frame"
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-red-400 transition hover:bg-black/70 hover:text-red-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Camera + mic pickers */}
      <div className="mt-3 flex items-center justify-center gap-4">
        <div className="flex items-center gap-1">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white">
            <Camera className="h-5 w-5" />
          </span>
          <DevicePicker kind="camera" selectedId={cameraId} onSelect={onCamera} direction="down" />
        </div>
        <div className="flex items-center gap-1">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white">
            <Mic className="h-5 w-5" />
          </span>
          <DevicePicker kind="microphone" selectedId={microphoneId} onSelect={onMic} direction="down" />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Small UI bits ---------------- */

function Panel({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <h2 className="mb-4 text-base font-bold text-white">{title}</h2>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between text-sm text-slate-200">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-5 w-5 accent-blue-600"
      />
    </label>
  );
}
